import React, { useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import { publicationsService } from '../../../services/publicationsService';
import { storageService } from '../../../services/storageService';

const PublicationsManager = () => {
	const [publications, setPublications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingPublication, setEditingPublication] = useState(null);
	const [showAddForm, setShowAddForm] = useState(false);
	const [formData, setFormData] = useState({
		title: '',
		slug: '',
		excerpt: '',
		content: '',
		author_name: '',
		publication_type: 'article',
		read_time_minutes: 5,
		publication_date: new Date().toISOString().split('T')[0],
		image_url: '',
		external_url: '',
		is_published: true,
	});

	useEffect(() => {
		loadPublications();
	}, []);

	const loadPublications = async () => {
		try {
			setLoading(true);
			const { data, error } = await publicationsService.getPublications(true); // adminMode = true
			if (error) throw error;
			setPublications(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error('Error loading publications:', error);
			setPublications([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (editingPublication) {
				// Update existing publication
				console.log('Updating publication with ID:', editingPublication.id);
				console.log('Update data:', formData);
				const updatedPublication = { ...editingPublication, ...formData };
				const result = await publicationsService.updatePublication(
					editingPublication.id,
					updatedPublication
				);
				console.log('Update result:', result);
				alert('Publication updated successfully!');
			} else {
				// Create new publication
				console.log('Creating new publication with data:', formData);
				const result = await publicationsService.createPublication(formData);
				console.log('Create result:', result);
				alert('Publication created successfully!');
			}

			resetForm();
			loadPublications();
		} catch (error) {
			console.error('Error saving publication:', error);
			alert('Error saving publication: ' + error.message);
		}
	};

	const handleEdit = (publication) => {
		console.log('Editing publication:', publication);
		setEditingPublication(publication);
		setFormData(publication);
		setShowAddForm(true);
	};

	const handleDelete = async (publicationId) => {
		if (!confirm('Are you sure you want to delete this publication?')) {
			return;
		}

		try {
			await publicationsService.deletePublication(publicationId);
			alert('Publication deleted successfully!');
			loadPublications();
		} catch (error) {
			console.error('Error deleting publication:', error);
			alert('Error deleting publication: ' + error.message);
		}
	};

	const resetForm = () => {
		setFormData({
			title: '',
			slug: '',
			excerpt: '',
			content: '',
			author_name: '',
			publication_type: 'article',
			read_time_minutes: 5,
			publication_date: new Date().toISOString().split('T')[0],
			image_url: '',
			external_url: '',
			is_published: true,
		});
		setEditingPublication(null);
		setShowAddForm(false);
	};

	const handleImageUpload = async (file) => {
		try {
			const { data, error } = await storageService.uploadImage(file, 'images', 'publications/');

			if (error) {
				console.error('Upload error:', error);
				alert('Upload failed: ' + error.message);
				return;
			}

			if (data) {
				setFormData((prev) => ({ ...prev, image_url: data.publicUrl }));
				alert('Image uploaded successfully!');
			}
		} catch (error) {
			console.error('Upload error:', error);
			alert('Upload failed: ' + error.message);
		}
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex items-center justify-between'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Publications Manager</h1>
						<p className='text-gray-600'>Manage articles, case studies, and legal publications</p>
					</div>
					<button
						onClick={() => setShowAddForm(true)}
						className='bg-[#800020] text-white px-4 py-2 rounded-md hover:bg-[#6b001a] transition-colors'
					>
						<Icon name='Plus' className='h-4 w-4 inline mr-2' />
						Add New Publication
					</button>
				</div>
			</div>

			{/* Add/Edit Form */}
			{showAddForm && (
				<div className='bg-white shadow rounded-lg p-6'>
					<h2 className='text-lg font-semibold text-gray-900 mb-4'>
						{editingPublication ? 'Edit Publication' : 'Add New Publication'}
					</h2>

					<form onSubmit={handleSubmit} className='space-y-4'>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Title *</label>
								<input
									type='text'
									value={formData.title}
									onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Slug *</label>
								<input
									type='text'
									value={formData.slug}
									onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='article-title'
									required
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Excerpt</label>
							<textarea
								value={formData.excerpt}
								onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))}
								rows={3}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Brief description of the publication...'
							/>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Content</label>
							<textarea
								value={formData.content}
								onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
								rows={8}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Full article content...'
							/>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>External URL</label>
							<input
								type='url'
								value={formData.external_url}
								onChange={(e) => setFormData((prev) => ({ ...prev, external_url: e.target.value }))}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='https://example.com/article'
							/>
							<p className='text-sm text-gray-500 mt-1'>
								Optional: Link to external article, journal, or publication
							</p>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Author Name</label>
								<input
									type='text'
									value={formData.author_name}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, author_name: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Type</label>
								<select
									value={formData.publication_type}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, publication_type: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								>
									<option value='article'>Article</option>
									<option value='case-study'>Case Study</option>
									<option value='news'>News</option>
									<option value='legal-update'>Legal Update</option>
								</select>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Read Time (minutes)
								</label>
								<input
									type='number'
									value={formData.read_time_minutes}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											read_time_minutes: parseInt(e.target.value),
										}))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									min='1'
								/>
							</div>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Publication Date
								</label>
								<input
									type='date'
									value={formData.publication_date}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, publication_date: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Image URL</label>
								<input
									type='text'
									value={formData.image_url}
									onChange={(e) => setFormData((prev) => ({ ...prev, image_url: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='https://your-project.supabase.co/storage/v1/object/public/images/...'
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Upload Image</label>
							<input
								type='file'
								accept='image/*'
								onChange={(e) => {
									if (e.target.files[0]) {
										handleImageUpload(e.target.files[0]);
									}
								}}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
							/>
						</div>

						<div className='flex items-center'>
							<input
								type='checkbox'
								id='is_published'
								checked={formData.is_published}
								onChange={(e) =>
									setFormData((prev) => ({ ...prev, is_published: e.target.checked }))
								}
								className='h-4 w-4 text-[#800020] focus:ring-[#800020] border-gray-300 rounded'
							/>
							<label htmlFor='is_published' className='ml-2 block text-sm text-gray-900'>
								Published
							</label>
						</div>

						<div className='flex justify-end space-x-3'>
							<button
								type='button'
								onClick={resetForm}
								className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors'
							>
								Cancel
							</button>
							<button
								type='submit'
								className='px-4 py-2 bg-[#800020] text-white rounded-md hover:bg-[#6b001a] transition-colors'
							>
								{editingPublication ? 'Update Publication' : 'Create Publication'}
							</button>
						</div>
					</form>
				</div>
			)}

			{/* Publications List */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Current Publications</h2>

				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading publications...</span>
					</div>
				) : publications.length === 0 ? (
					<div className='text-center py-8 text-gray-500'>
						<Icon name='FileText' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
						<p>No publications found</p>
					</div>
				) : (
					<div className='space-y-4'>
						{publications.map((publication) => (
							<div key={publication.id} className='border border-gray-200 rounded-lg p-4'>
								<div className='flex items-start justify-between'>
									<div className='flex-1'>
										<div className='flex items-center space-x-4'>
											<div className='w-20 h-16 bg-gray-100 rounded overflow-hidden'>
												{publication.image_url ? (
													<img
														src={publication.image_url}
														alt={publication.title}
														className='w-full h-full object-cover'
													/>
												) : (
													<div className='w-full h-full flex items-center justify-center text-gray-400'>
														<Icon name='FileText' className='h-6 w-6' />
													</div>
												)}
											</div>
											<div className='flex-1'>
												<h3 className='font-medium text-gray-900'>{publication.title}</h3>
												<p className='text-sm text-gray-600'>{publication.excerpt}</p>
												<div className='flex items-center mt-2 space-x-4'>
													<span className='text-xs text-gray-500'>
														By {publication.author_name}
													</span>
													<span className='text-xs text-gray-500'>
														{publication.read_time_minutes} min read
													</span>
													<span className='text-xs text-gray-500'>
														{publication.publication_date}
													</span>
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															publication.is_published
																? 'bg-green-100 text-green-800'
																: 'bg-red-100 text-red-800'
														}`}
													>
														{publication.is_published ? 'Published' : 'Draft'}
													</span>
												</div>
											</div>
										</div>
									</div>
									<div className='flex space-x-2'>
										<button
											onClick={() => handleEdit(publication)}
											className='text-[#800020] hover:text-[#6b001a] transition-colors'
										>
											<Icon name='Edit' className='h-4 w-4' />
										</button>
										<button
											onClick={() => handleDelete(publication.id)}
											className='text-red-600 hover:text-red-700 transition-colors'
										>
											<Icon name='Trash2' className='h-4 w-4' />
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default PublicationsManager;
